import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addColorPalettes } from "../redux/reducers/colorPalette";
import {
  setWheelSegments,
  updateSegmentColorPalette,
  pushToHistoryAction,
} from "../redux/reducers/segmentBuilder";
import {
  getWheelId,
  getWheelSegments,
} from "../redux/selectors/segmentBuilderSelectors";
import {
  generateScheme,
  savePalette,
  generateColorsFromImage,
} from "../services/api";
import { getRandomId } from "../utils/helpers";
import useSegmentSettings from "./useSegmentSetting";
import {
  PALETTE_TYPES,
  PALETTE_REGEN_ONLY_ON_COLOR_CHANGE,
} from "../components/SegmentBuilder/Shared/ColorPalette/constants";
import { beginPaletteWork, endPaletteWork } from "./paletteInFlight";
// Pure decision helpers live in ./paletteRegenDecision (kept dependency-free so
// the no-write-storm guard + the deliberate-reroll bypass are unit-testable
// without the heavy builder module graph).
import {
  shouldRegeneratePalette,
  shouldRegenerateFillPalette,
} from "./paletteRegenDecision";
// Segment Palette FROM THE FILL (flag REACT_APP_SEGMENT_PALETTE_COLOR, default
// OFF): reuse the Palette Generator's image->palette extractor (generateColorsFromImage)
// for image/gif fills, and the gradient stops / pattern colors for the rest.
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";
import {
  resolveSegmentPaletteSeed,
  buildFillPalette,
} from "../components/SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteFromFill";
// D18 (flag REACT_APP_SEG_PALETTE_AUTODERIVE): a debounce + undo/history
// safety-net + "Segment colors updated to match your image — Undo" toast layered ON TOP of the
// fill-derive engine above. Flag OFF -> none of this runs (byte-identical). It
// also, on its own, enables the fill-derive engine (see the guard in
// fetchSegmentPaletteFromFill) so D18 works without REACT_APP_SEGMENT_PALETTE_COLOR.
import { SEG_PALETTE_AUTODERIVE } from "../constants/featureFlags";
import {
  isPaletteAutoDeriveLocked,
  makeDebouncer,
  AUTODERIVE_DEBOUNCE_MS,
} from "../components/SegmentBuilder/Shared/ColorPalette/autoDerivePaletteLogic";
import { FEATURES } from "../constants/features";
import { useIfUserEligible } from "./useIfUserEligible";
import { showAutoDeriveUndoToast } from "../components/SegmentBuilder/Shared/ColorPalette/AutoDeriveUndoToast";


/**
 * Top up a fill's already-ORDERED color list (pattern fg/bg, or a gradient's
 * <5 stops) to exactly 5 colors, seeded on index0 (the locked Segment Color —
 * pattern foreground / gradient first-stop) via the SAME OKLCH generate-scheme
 * the solid-color path uses (Gordon 2026-09-06 verbatim: "fill the remaining
 * colours from the OKLCH scheme"). index0 is left untouched; scheme colors
 * that already appear (case-insensitively) are skipped so the fill's real
 * colors are never duplicated. Never throws — a failed/short scheme response
 * just leaves the palette shorter than 5 rather than blocking the fill commit.
 *
 * @param {string[]} hexes 1-4 already-ordered colors, index0 = the primary
 * @returns {Promise<string[]>}
 */
const topUpToFive = async (hexes) => {
  const out = [...hexes];
  try {
    const response = await generateScheme({
      hex: [out[0], null, null, null, null],
      mode: "auto",
      count: 5,
      scheme_count: 1,
      mood: "auto",
    });
    if (!response?.data?.success) return out;
    const schemeColors = (response.data.data?.[0]?.colors || [])
      .map((c) => (typeof c === "string" ? c : c?.hex?.value || c?.hex))
      .filter(Boolean);
    const have = new Set(out.map((h) => String(h).toLowerCase()));
    for (const c of schemeColors) {
      if (out.length >= 5) break;
      const key = String(c).toLowerCase();
      if (have.has(key)) continue;
      have.add(key);
      out.push(c);
    }
  } catch (error) {
    console.error("Error topping up fill palette from scheme:", error);
  }
  return out;
};

export const useSegmentPalettes = () => {
  const dispatch = useDispatch();
  const wheelId = useSelector(getWheelId);
  const wheelData = useSelector(getWheelSegments);
  const { getColor } = useSegmentSettings() || {};
  const inFlightRef = useRef(new Set());
  // vy-0915-dev-palette-generator-403-still-fires-for-prosuite: the image/gif
  // branch below reuses the Advanced Palette Generator's gated
  // generateColorsFromImage (POST /api/v2/wheel/image-palette-generator/,
  // FlagGatedAdvPaletteGenerator) but, unlike every other caller of this
  // feature (CreateWheelPopup, WheelPaletteTab, ColorPalette, SegmentColor —
  // all check useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR) first), used
  // to call it unconditionally on every fill change. For a tier the backend
  // has not enabled this feature for, that fired an unconditional 403 (with a
  // console error) on every segment select — a wasted round trip, not a
  // resolvable code bug in the entitlement chain itself.
  const {
    isEligible: isEligibleForPaletteGenerator,
    isEntitlementResolved: isPaletteEntitlementResolved,
  } = useIfUserEligible(FEATURES.ADV_PALLETE_GENERATOR);

  // Always-fresh mirror of the current segments so the memoized
  // fetchSegmentPalette can read the latest palette state WITHOUT putting
  // wheelData in its dependency array (which would re-create the callback on
  // every edit and defeat the in-flight dedup). The skip-if-unchanged guard
  // reads through this ref.
  const wheelDataRef = useRef(wheelData);
  useEffect(() => {
    wheelDataRef.current = wheelData;
  }, [wheelData]);

  const updateSegmentColors = async (palette) => {
    const data = [...wheelData];

    const updatedSegments = data.map((segment, ind) => {
      const { color } = getColor(data, ind, palette);
      return {
        ...segment,
        settings: { ...segment.settings, color },
      };
    });

    dispatch(setWheelSegments(updatedSegments));

    await Promise.all(
      updatedSegments.map((segment) =>
        fetchSegmentPalette(segment.settings.color, segment.id)
      )
    );
  };

  /**
   * Fetches a palette based on a given segment color.
   * Deduplicates concurrent calls for the same segment.
   * @param {string} color - The base color for the palette.
   * @param {string|number} segmentId - The ID of the segment.
   * @param {object} [opts]
   * @param {boolean} [opts.force=false] - When true, bypass the
   *   PALETTE_REGEN_ONLY_ON_COLOR_CHANGE idempotency guard and reroll even if the
   *   segment already has a palette for this exact color. This is ONLY set by the
   *   explicit manual "Regenerate" control (the deliberate reroll, PAL-002). The
   *   in-flight dedup below is still respected, so a force reroll can never fan
   *   out into N-parallel calls for the same segment+color.
   */
  const fetchSegmentPalette = useCallback(
    async (color, segmentId, opts = {}) => {
      if (!color) return;
      const { force = false } = opts;
      const key = `${segmentId}-${color}`;
      if (inFlightRef.current.has(key)) return;

      // Idempotency / de-storm guard (PAL fix): if this segment already has a
      // palette generated from this exact color, reuse it instead of firing a
      // fresh AI generate-scheme + a new save-palette row. This is what makes a
      // wheel-palette change (LW-01) stop regenerating palettes for every
      // segment, and what stops a segment's palette from being re-randomized
      // (new color ids) every time it is reselected. Deterministic-by-reuse.
      //
      // The explicit manual Regenerate control passes force=true to deliberately
      // skip this guard (a sanctioned reroll of the same color); the AUTO path
      // never passes force, so it stays stable.
      const segment = wheelDataRef.current?.find((s) => s.id == segmentId);
      if (
        !shouldRegeneratePalette(segment, color, {
          force,
          guardEnabled: PALETTE_REGEN_ONLY_ON_COLOR_CHANGE,
        })
      ) {
        return;
      }

      inFlightRef.current.add(key);
      // Mark palette work in flight so the autosave timer can defer a save
      // until this generate+persist round-trip lands (LW-02 de-race).
      beginPaletteWork();

      const hexes = [color, null, null, null, null];

      try {
        const response1 = await generateScheme({
          hex: hexes,
          mode: "auto",
          count: 5,
          scheme_count: 1,
          mood: "auto",
        });

        if (response1.data.success) {
          const finalData = transformPaletteData(response1.data.data, color);

          dispatch(addColorPalettes({
            paletteType:PALETTE_TYPES.SEGMENT,
            type:"color",
            paletteColors:finalData,
          }))

          const response2 = await savePalette({
            id: typeof finalData[0].id === 'string' ? undefined : finalData[0].id,
            colors: finalData[0].colors,
            palette_name: finalData[0].paletteName,
            wheel_id: wheelId,
            type: 2,
            usage_type: 2,
            tab_type:1,
            metadata: {
              color:color,
            },
          });

          if (response2.data.success) {
            const paletteId = response2.data.data.id;

            dispatch(
              updateSegmentColorPalette({
                palette: response2.data.data,
                paletteId,
                id: segmentId,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching segment palette:", error);
      } finally {
        inFlightRef.current.delete(key);
        endPaletteWork();
      }
    },
    [dispatch, wheelId]
  );

  /**
   * Explicit, user-initiated "Regenerate" of the current segment's palette — the
   * DELIBERATE reroll (PR #1711 / PAL-002). Unlike the auto path, this forces a
   * fresh AI generate-scheme even when the segment color is unchanged, so each
   * click yields one new option (variety). It is a SINGLE call per invocation:
   * never auto-fires (only on an explicit click), never N-parallel (the in-flight
   * dedup in fetchSegmentPalette guards the same segment+color, and the UI
   * disables the button while a regenerate is pending). Resolves when the
   * round-trip lands so callers can drive a disabled/in-flight button state.
   *
   * @param {string} color - the segment's current base color.
   * @param {string|number} segmentId - the segment to reroll.
   */
  const regenerateSegmentPalette = useCallback(
    (color, segmentId) =>
      fetchSegmentPalette(color, segmentId, { force: true }),
    [fetchSegmentPalette]
  );

  /**
   * (Part A, flag REACT_APP_SEGMENT_PALETTE_COLOR) Generate the Segment Palette
   * FROM THE FILL instead of only from the solid color:
   *   · Image / GIF -> feed the fill's url through the SAME Palette Generator
   *     extractor the "Palette Generator" surface already uses
   *     (generateColorsFromImage) — we do NOT re-implement extraction.
   *   · Gradient / Pattern -> its stops / fg-bg colors (no API).
   *   · solid Color / no fill -> delegate to the existing color path
   *     (fetchSegmentPalette).
   * Persists onto the segment (savePalette + updateSegmentColorPalette) exactly
   * like the color path, so settings.paletteData drives getPaletteColor and the
   * palette-derived Segment Color.
   *
   * Flag OFF -> pure delegate to the legacy color path (byte-identical). In-flight
   * dedup keyed by segment + seed so a re-commit of the same fill can't fan out
   * into N-parallel (paid) image calls.
   *
   * MOTION LOCK (Gordon 2026-09-06 verbatim): a GIF/loop's motion must NOT
   * re-trigger a derive once the palette has been generated for the CURRENT
   * fill identity — see shouldRegenerateFillPalette. The persisted palette
   * carries `fillSeedSig` (the exact seed signature it was derived from); as
   * long as a re-derive call's signature matches, it is a no-op. Replacing the
   * wedge fill (new url / new gradient-or-pattern colors) changes the
   * signature and unlocks exactly one fresh derive.
   *
   * @param {object} segmentSettings the segment's `settings` (with .fill / legacy fields)
   * @param {string|number} segmentId
   * @param {{force?:boolean}} [opts] force bypasses the motion-lock (unused today,
   *   mirrors fetchSegmentPalette's shape for a future manual fill-Regenerate).
   */
  const fetchSegmentPaletteFromFill = useCallback(
    async (segmentSettings, segmentId, opts = {}) => {
      const s = segmentSettings || {};
      // The fill-derive engine is enabled by EITHER the original fill-color flag
      // OR the D18 auto-derive flag. With both OFF, byte-identical legacy path.
      if (!SEGMENT_PALETTE_COLOR_ENABLED && !SEG_PALETTE_AUTODERIVE) {
        return fetchSegmentPalette(s.color, segmentId);
      }

      // D18: respect an existing manual palette lock (today: the
      // `segmentColorManual` override marker). A manual pick wins over a
      // media-driven auto-change. Only enforced under the D18 flag so the
      // original feature's behaviour is unchanged.
      if (SEG_PALETTE_AUTODERIVE && isPaletteAutoDeriveLocked({ settings: s })) {
        return;
      }

      const seed = resolveSegmentPaletteSeed(s);
      // Solid color / nothing to extract -> the existing color path (which has
      // its own idempotency + save + updateSegmentColorPalette).
      if (seed.kind === "color") {
        return fetchSegmentPalette(seed.color, segmentId);
      }

      const seedSig =
        seed.kind === "image" ? seed.url : (seed.colors || []).join(",");

      // Motion lock: a derive already committed for this EXACT fill identity
      // (image url / gradient+pattern color signature) is not redone — this is
      // what stops a GIF's frame/motion advancing from ever re-firing the
      // extractor while the wedge fill itself is unchanged.
      const segment = wheelDataRef.current?.find((seg) => seg.id == segmentId);
      if (
        !shouldRegenerateFillPalette(segment, seedSig, {
          force: !!opts.force,
        })
      ) {
        return;
      }

      const key = `${segmentId}-fill-${seedSig}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      beginPaletteWork();

      try {
        let hexes;
        let paletteName = "Fill palette";
        if (seed.kind === "image") {
          // Don't spend a round trip on a gated endpoint the backend has
          // already told us this tier can't use — same fail-closed-once-
          // resolved shape as the other ADV_PALLETE_GENERATOR call sites.
          // Unresolved (isPaletteEntitlementResolved false) still proceeds,
          // matching isEligible's existing fail-OPEN default elsewhere.
          if (isPaletteEntitlementResolved && !isEligibleForPaletteGenerator) {
            return;
          }
          // REUSE the Palette Generator's image/gif -> palette extraction.
          const response = await generateColorsFromImage(seed.url, 5);
          if (!response?.data?.success) return;
          const data = response.data.data || {};
          paletteName = data.palette_name || paletteName;
          hexes = (data.colors || [])
            .map((c) =>
              typeof c === "string" ? c : c?.hex?.value || c?.hex || null
            )
            .filter(Boolean);
        } else {
          // seed.kind === "colors" (gradient stops / pattern colors), already
          // ORDERED so index0 is the correct Segment Color (foreground for a
          // pattern, first stop for a gradient — Gordon 2026-09-06 verbatim).
          hexes = [...seed.colors];
          // Fewer than 5 real colors (a 2-color pattern fg/bg, or a gradient
          // with <5 stops) -> top up from the SAME OKLCH generate-scheme the
          // solid-color path uses, seeded on index0 so the fill's remaining
          // slots stay harmonized with the locked primary.
          if (hexes.length && hexes.length < 5) {
            hexes = await topUpToFive(hexes);
          }
        }

        const palette = buildFillPalette(hexes, {
          paletteName,
          makeId: getRandomId,
        });
        if (!palette.colors.length) return;

        dispatch(
          addColorPalettes({
            paletteType: PALETTE_TYPES.SEGMENT,
            type: "color",
            paletteColors: [palette],
          })
        );

        const response2 = await savePalette({
          id: typeof palette.id === "string" ? undefined : palette.id,
          colors: palette.colors,
          palette_name: palette.paletteName,
          wheel_id: wheelId,
          type: 2,
          usage_type: 2,
          tab_type: 1,
          metadata: { source: "fill" },
        });

        if (response2?.data?.success) {
          const saved = response2.data.data;
          // D18 safety-net: push a discrete undo/history entry BEFORE the palette
          // lands, so the "colors changed without a click" auto-change is one
          // clean Undo. Guarded by the D18 flag; the original feature already
          // records the fill commit via withHistory at the call site.
          if (SEG_PALETTE_AUTODERIVE) {
            dispatch(pushToHistoryAction());
          }
          dispatch(
            updateSegmentColorPalette({
              // Tag the persisted palette as fill-derived so the Segment Color
              // resolver knows the primary is the fill's dominant color, and
              // record the seed signature it was derived from (the motion-lock
              // check above compares against this on the NEXT derive call).
              palette: { ...saved, source: "fill", fillSeedSig: seedSig },
              paletteId: saved.id,
              id: segmentId,
            })
          );
          // Only the IMAGE/GIF path is a "changed without a click" surprise worth
          // an explicit Undo toast; gradient/pattern derive from colors the user
          // just picked. Flag OFF -> no toast.
          if (SEG_PALETTE_AUTODERIVE && seed.kind === "image") {
            showAutoDeriveUndoToast(dispatch);
          }
        }
      } catch (error) {
        console.error("Error fetching segment palette from fill:", error);
      } finally {
        inFlightRef.current.delete(key);
        endPaletteWork();
      }
    },
    [
      dispatch,
      wheelId,
      fetchSegmentPalette,
      isEligibleForPaletteGenerator,
      isPaletteEntitlementResolved,
    ]
  );

  // D18: a trailing-debounced fill-derive so rapid successive media selections
  // collapse to a SINGLE derivation (from the last pick). One debouncer per hook
  // instance. When the flag is OFF this is never used by callers, but it still
  // delegates straight through so it is always safe to call.
  const fillDebouncerRef = useRef(makeDebouncer(AUTODERIVE_DEBOUNCE_MS));
  const fetchSegmentPaletteFromFillDebounced = useCallback(
    (segmentSettings, segmentId) => {
      if (!SEG_PALETTE_AUTODERIVE) {
        return fetchSegmentPaletteFromFill(segmentSettings, segmentId);
      }
      fillDebouncerRef.current.run(() =>
        fetchSegmentPaletteFromFill(segmentSettings, segmentId)
      );
    },
    [fetchSegmentPaletteFromFill]
  );

  return {
    fetchSegmentPalette,
    fetchSegmentPaletteFromFill,
    fetchSegmentPaletteFromFillDebounced,
    updateSegmentColors,
    regenerateSegmentPalette,
  };
};

/**
 * Transforms palette data into the required format.
 */
const transformPaletteData = (data, color) => {
  return data.map((item, i) => ({
    index: i,
    paletteName: item.palette_name,
    color,
    colors: item.colors.map((color, ind) => ({
      hex: color.hex.value,
      id: getRandomId(),
      selected: false,
      locked: ind === 0, // Lock the first color which is the segment color
      index: ind,
    })),
    id: getRandomId(),
    selected: i === 0,
  }));
};
